import React, { useState, useEffect, useMemo } from 'react';
import './checkStock.css';

const formatPrice = (price) => {
    return `${parseFloat(price).toFixed(2)} RON`;
};

const PriceCell = ({ variant }) => {
    const hasDiscount = variant.compare_at_price && parseFloat(variant.compare_at_price) > parseFloat(variant.price);
    
    return (
        <div className="price-cell">
            <div className="price-info">
                <div className="current-stock">
                    <span className="stock-count">{variant.inventory_quantity}</span>
                    <span className="stock-label">in stock</span>
                </div>
            </div>
            <div className="price-details">
                {hasDiscount ? (
                    <>
                        <div className="price-row">
                            <span className="price-label">REGULAR</span>
                            <span className="compare-price">{formatPrice(variant.compare_at_price)}</span>
                        </div>
                        <div className="price-row">
                            <span className="price-label">SALE</span>
                            <span className="current-price">{formatPrice(variant.price)}</span>
                        </div>
                    </>
                ) : (
                    <div className="price-row">
                        <span className="price-label">PRICE</span>
                        <span className="current-price">{formatPrice(variant.price)}</span>
                    </div>
                )}
            </div>
        </div>
    );
};

const Pricing = ({ inventory, coreProducts }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedVariants, setSelectedVariants] = useState([]);

    // Get all unique variant titles from the inventory
    const allVariants = useMemo(() => {
        const variantSet = new Set();
        inventory.forEach(product => {
            product.variants.forEach(variant => {
                variantSet.add(variant.title);
            });
        });
        return Array.from(variantSet).sort();
    }, [inventory]);

    // Filter inventory to show only core products
    const coreInventory = useMemo(() => {
        return inventory.filter(product => coreProducts[product.product_id]);
    }, [inventory, coreProducts]);

    // Filter based on search term
    const filteredInventory = useMemo(() => {
        const filtered = !searchTerm.trim() 
            ? coreInventory 
            : coreInventory.filter(product => 
                product.product_title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                product.variants.some(variant => variant.sku?.toLowerCase().includes(searchTerm.toLowerCase()))
            );

        // Sort by SKU
        return filtered.sort((a, b) => {
            const skuA = a.variants[0]?.sku || '';
            const skuB = b.variants[0]?.sku || '';
            return skuA.localeCompare(skuB);
        });
    }, [coreInventory, searchTerm]);

    // Handle variant selection
    const handleVariantToggle = (variantTitle) => {
        setSelectedVariants(prev => {
            if (prev.includes(variantTitle)) {
                return prev.filter(v => v !== variantTitle);
            }
            return [...prev, variantTitle];
        });
    };

    // Reset selected variants when inventory changes
    React.useEffect(() => {
        setSelectedVariants(allVariants.slice(0, 4)); // Default to first 4 variants
    }, [allVariants]);

    return (
        <div className="check-stock-container">
            <div className="stock-container">
                <div className="po-header">
                    <h2>Pricing Overview</h2>
                </div>
                
                <div className="controls-container">
                    <div className="search-container">
                        <input
                            type="text"
                            placeholder="Search by product name or SKU..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="search-input"
                        />
                    </div>

                    <div className="variant-selector">
                        <h4>Select Variants to Display:</h4>
                        <div className="variant-options">
                            {allVariants.map(variant => (
                                <label key={variant} className="variant-checkbox">
                                    <input
                                        type="checkbox"
                                        checked={selectedVariants.includes(variant)}
                                        onChange={() => handleVariantToggle(variant)}
                                    />
                                    {variant}
                                </label>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="inventory-table-container">
                    <table className="inventory-table">
                        <thead>
                            <tr>
                                <th>Image</th>
                                <th>Product Name</th>
                                {selectedVariants.map(variant => (
                                    <th key={variant}>{variant}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {filteredInventory.map((product) => (
                                <tr key={product.product_id}>
                                    <td className="image-cell">
                                        {product.image_url ? (
                                            <img 
                                                src={product.image_url} 
                                                alt={product.product_title}
                                                style={{
                                                    width: '50px',
                                                    height: '50px',
                                                    objectFit: 'contain'
                                                }}
                                            />
                                        ) : (
                                            <div className="no-image">No Image</div>
                                        )}
                                    </td>
                                    <td>
                                        <div className="product-info">
                                            <div className="product-title">{product.product_title}</div>
                                            <div className="product-sku">{product.variants[0]?.sku}</div>
                                        </div>
                                    </td>
                                    {selectedVariants.map(variantTitle => {
                                        const variant = product.variants.find(v => v.title === variantTitle);
                                        return (
                                            <td key={variantTitle} className="variant-cell">
                                                {variant ? (
                                                    <PriceCell variant={variant} />
                                                ) : '-'}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default Pricing; 